import React from "react";
import { Quote, Star } from "lucide-react";
import OrnamentalDivider from "./OrnamentalDivider";
import { ASSETS } from "../../lib/api";

const TESTIMONIALS = [
    {
        quote:
            "Guruji doesn't just teach music — he teaches how to listen, how to breathe, how to live. Every session at the Gurukula feels like a small sadhana.",
        name: "Aaradhya Sharma",
        role: "Shishya · Hindustani Vocal, 4 years",
    },
    {
        quote:
            "My daughter has bloomed in ways I never imagined. The values she has absorbed here — discipline, humility, seva — are the true gift of this Gurukula.",
        name: "Ramesh Kulkarni",
        role: "Parent · Children's Foundation Program",
    },
    {
        quote:
            "As a working professional in another city, the online classes have been a lifeline. The Guru-Shishya bond somehow crosses the screen intact — a rare thing.",
        name: "Kavya Iyer",
        role: "Online Student · Bengaluru → New Jersey",
    },
    {
        quote:
            "I came searching for ragas. I found silence, and inside that silence — music. Shri Ananya Bhargava is not merely a teacher, he is a way.",
        name: "Vikram Rao",
        role: "Advanced Shishya · Performance Training",
    },
];

export default function Testimonials() {
    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="section-pad bg-ivory"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                    <div className="eyebrow">· ಶಿಷ್ಯರ ನುಡಿ · Voices from the Gurukula ·</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-maroon mt-4 leading-tight">
                        What our shishyas &
                        <span className="italic text-gold"> parents say.</span>
                    </h2>
                    <p className="font-serif text-lg text-ink/80 max-w-3xl mx-auto mt-5 leading-relaxed">
                        A few words from the students, parents and rasikas who walk this
                        path with us — from Bengaluru to across the seas.
                    </p>
                </div>

                <OrnamentalDivider className="mb-14" />

                {/* Live riyaz session hero */}
                <div className="relative mb-14 overflow-hidden border border-gold/30" data-testid="testimonials-riyaz-image">
                    <img
                        src={ASSETS.riyazSession}
                        alt="A live discussion & riyaz session at Shri Vishwa Bandhu Sangeetha Gurukula — Guruji Shri Ananya Bhargava with harmonium, students seated in the Guru-Shishya tradition"
                        className="w-full h-[380px] lg:h-[520px] object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/95 via-maroon-900/25 to-transparent" />
                    <figcaption className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-ivory">
                        <div className="eyebrow text-gold-300">· A Living Guru-Shishya Parampara ·</div>
                        <div className="font-display italic text-2xl lg:text-3xl text-ivory mt-2 max-w-3xl leading-snug">
                            A discussion & riyaz session with Guruji — where every question becomes a note,
                            and every note becomes a lesson.
                        </div>
                    </figcaption>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {TESTIMONIALS.map((t, i) => (
                        <blockquote
                            key={i}
                            data-testid={`testimonial-${i}`}
                            className="card-cream p-8 lg:p-10 flex flex-col"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div className="w-12 h-12 flex items-center justify-center bg-maroon text-gold-300">
                                    <Quote size={20} />
                                </div>
                                <div className="flex items-center gap-1 text-gold">
                                    {[0, 1, 2, 3, 4].map((s) => (
                                        <Star key={s} size={14} fill="currentColor" strokeWidth={0} />
                                    ))}
                                </div>
                            </div>
                            <p className="font-display italic text-2xl text-maroon leading-snug flex-1">
                                “{t.quote}”
                            </p>
                            <footer className="mt-8 pt-6 border-t border-gold-100">
                                <div className="font-display text-xl text-maroon">
                                    — {t.name}
                                </div>
                                <div className="eyebrow text-gold mt-1">{t.role}</div>
                            </footer>
                        </blockquote>
                    ))}
                </div>

                <div className="mt-14 text-center">
                    <a
                        href="#contact"
                        data-testid="testimonials-share-cta"
                        className="btn-ghost-gold inline-flex items-center gap-2"
                    >
                        Share your experience with the Gurukula
                    </a>
                </div>
            </div>
        </section>
    );
}
